import { Alert, Button, Typography } from '@execonline-inc/execonline-ui';
import { assertNever } from '@kofno/piper';
import clsx from 'clsx';
import { just, Maybe, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { T, TPlainTextKey } from '../../../../../Translations';
import { licenseAlertsStore } from '../LicenseAlerts/LicenseAlertsStore';
import { LicenseAlerts, LicenseAlertsState } from '../LicenseAlerts/LicenseAlertsStore/Types';
import { unrecognizedEmails } from '../LicenseAlerts/ParticipantLists';
import {
  AlertSeverity,
  invitedParticipantsWithGaps,
  participantsStartingLater,
  peopleWithoutFullAccess,
  severityOf,
  soleParticipant,
  soleParticipantStartingLater,
  unlicensedParticipantsWithGaps,
} from '../LicenseAlerts/Selectors';
import { shareStore } from '../Stores/ShareStore';
import { treatmentOf } from '../LicenseAlerts/HiddenCourseTreatment';
import { UnlicensedAccess } from '../LicenseAlerts/HiddenCourseTreatment';
import { LaterStarts, startsLaterBanner, startsLaterHeadline } from './StartsLaterBanner';

interface Props {
  onViewDetails: () => void;
}

interface GapSummary {
  kind: 'gaps';
  severity: AlertSeverity;
  withGaps: number;
  soleName: Maybe<string>;
  access: UnlicensedAccess;
  gaps: Gaps;
  // Someone missing a course can also be starting later; the banner says so
  // rather than leaving the date to the details.
  later: LaterStarts;
}

// Nobody is missing a course, but some people's licenses only start later
// (DVLP-8068). They are invited; the banner says when their access begins.
interface StartsLaterSummary extends LaterStarts {
  kind: 'starts-later';
}

type Summary = GapSummary | StartsLaterSummary;

interface HeldSummary {
  summary: Summary;
  // True while a fresher result is on its way and this one is being kept only
  // to hold the space.
  reloading: boolean;
}

// Only a loaded result with something to report earns the banner. Before the
// first result it stays away rather than flickering a warning on and off, and a
// list where everyone can enroll in everything needs none.
const summaryOf = (state: LicenseAlertsState): Maybe<Summary> => {
  switch (state.kind) {
    case 'waiting':
    case 'loading':
    case 'error':
      return nothing();
    case 'ready': {
      const alerts: LicenseAlerts = state.licenseAlertsResource.payload;
      const outsiders = unrecognizedEmails(alerts, shareStore.participants).length;
      // Addresses outside the organization cannot enrol either, so they belong
      // in the headline count rather than only inside the modal.
      const withGaps = peopleWithoutFullAccess(alerts, outsiders);

      const later: LaterStarts = {
        count: participantsStartingLater(alerts),
        sole: soleParticipantStartingLater(alerts),
      };

      if (withGaps === 0) {
        return later.count === 0 ? nothing() : just<Summary>({ kind: 'starts-later', ...later });
      }

      return just<Summary>({
        kind: 'gaps',
        severity: severityOf(alerts, outsiders),
        withGaps,
        soleName: soleParticipant(alerts, outsiders).map((participant) => participant.firstName),
        access: treatmentOf(shareStore.state),
        gaps: gapsFor(alerts, outsiders),
        later,
      });
    }
    default:
      return assertNever(state);
  }
};

// A group is sent by id and expands server-side, so its members are invited
// without ever appearing in verifiedUserIds. Counting by id would report them
// as dropped, so a list containing any group reports nobody excluded.
const gapsFor = (alerts: LicenseAlerts, outsiders: number): Gaps => {
  const withoutFullAccess = peopleWithoutFullAccess(alerts, outsiders);

  const invited =
    shareStore.verifiedGroupIds.length > 0
      ? withoutFullAccess
      : invitedParticipantsWithGaps(alerts, shareStore.verifiedUserIds);

  return {
    invited,
    excluded: withoutFullAccess - invited,
    unlicensed: unlicensedParticipantsWithGaps(alerts),
    outsiders,
  };
};

const statusFor = (severity: AlertSeverity): 'warning' | 'error' => {
  switch (severity) {
    case 'blocked':
      return 'error';
    case 'clear':
    case 'mixed':
    case 'limited':
      return 'warning';
    default:
      return assertNever(severity);
  }
};

// The people who cannot enroll are not all the same. Someone holding a license
// that grants none of this collection is invited and lands in it; someone with no
// license, or an address matching nobody, is dropped before the invitation is sent
// (DVLP-7735) and lands nowhere. "They can join" is only ever true of the first.
const notInvitedBody = (unlicensed: number, outsiders: number): TPlainTextKey => {
  if (unlicensed === 0) {
    return "They aren't in your organization, so they won't receive an invitation.";
  }

  if (outsiders === 0) {
    return "They won't receive an invitation until someone assigns them a license.";
  }

  return "They won't receive an invitation: some hold no license, some aren't in your organization.";
};

// Nobody can enroll in anything. What that means depends on how the collection
// treats unlicensed content - but only for people who are actually invited.
const blockedBody = (access: UnlicensedAccess): TPlainTextKey => {
  switch (access) {
    case 'preview':
      return "Everyone invited can join and preview the courses but can't enroll.";
    case 'allocate':
      return 'Everyone invited is assigned a license automatically.';
    case 'block':
      return "Everyone invited can join the collection but can't enroll in anything.";
    default:
      return assertNever(access);
  }
};

interface Gaps {
  // People who cannot enroll in everything but are invited regardless.
  invited: number;
  // People who cannot enroll in everything and will not be invited at all.
  excluded: number;
  unlicensed: number;
  outsiders: number;
}

const bodyFor = (
  severity: AlertSeverity,
  access: UnlicensedAccess,
  gaps: Gaps,
  soleName: Maybe<string>,
): TPlainTextKey => {
  // Anyone dropped from the invite is the most important thing on the banner,
  // whatever the licensing looks like: "they can join the collection" is false
  // for them, and saying it of the group says it of them too.
  if (gaps.excluded > 0 && gaps.invited === 0) {
    return notInvitedBody(gaps.unlicensed, gaps.outsiders);
  }

  switch (severity) {
    case 'clear':
    case 'mixed':
      return "They can join the collection but can't enroll in every course.";
    case 'limited':
      // A headline naming one person reads oddly over "everyone".
      return soleName.isJust()
        ? "They can join the collection but can't enroll in every course."
        : 'Everyone can join, but no one can enroll in every course.';
    case 'blocked':
      return blockedBody(access);
    default:
      return assertNever(severity);
  }
};

// Every participant added reloads the alerts. Dropping the banner for the
// length of that request and putting it back moves everything below it twice,
// which is worse than briefly showing a count that is about to be replaced.
// The footprint stays; the content refreshes in place.
const useHeldSummary = (state: LicenseAlertsState): Maybe<HeldSummary> => {
  const held = React.useRef<Maybe<Summary>>(nothing());
  const current = summaryOf(state);

  switch (state.kind) {
    case 'ready':
      held.current = current;

      return current.map((summary) => ({ summary, reloading: false }));
    case 'loading':
      return held.current.map((summary) => ({ summary, reloading: true }));
    case 'waiting':
    case 'error':
      held.current = nothing();

      return nothing();
    default:
      return assertNever(state);
  }
};

function LicenseAlertBanner({ onViewDetails }: Props) {
  return useHeldSummary(licenseAlertsStore.state)
    .map(({ summary, reloading }) => {
      if (summary.kind === 'starts-later') {
        return startsLaterBanner(summary, reloading, onViewDetails);
      }

      const headline: React.ReactNode = summary.soleName.cata<React.ReactNode>({
        // One person: name them rather than counting to one.
        Just: (firstName) =>
          summary.severity === 'blocked' ? (
            <T kind="{{name}} isn't licensed for these courses" name={firstName} />
          ) : (
            <T kind="{{name}} isn't fully licensed" name={firstName} />
          ),
        Nothing: () => {
          switch (summary.severity) {
            case 'clear':
            case 'mixed':
              return summary.withGaps === 1 ? (
                <T kind="{{withGaps}} person isn't fully licensed" withGaps={summary.withGaps} />
              ) : (
                <T kind="{{withGaps}} people aren't fully licensed" withGaps={summary.withGaps} />
              );
            case 'limited':
              return <T kind="No one is fully licensed" />;
            case 'blocked':
              return <T kind="No one is licensed for these courses" />;
            default:
              return assertNever(summary.severity);
          }
        },
      });

      return (
        <Alert
          key="banner"
          status={statusFor(summary.severity)}
          variant="outlined"
          icon
          radius="md"
          className="mb-6"
          aria-busy={reloading}
          data-intercom-target="share-license-alert-banner"
        >
          <div
            className={clsx(
              'flex w-full flex-wrap items-center gap-x-4 gap-y-2 transition-opacity',
              reloading && 'opacity-60',
            )}
          >
            <div className="min-w-0 flex-1">
              <Typography variant="bodyMedium" fontWeight="medium">
                {headline}
              </Typography>
              <Typography variant="bodySmall" className="text-muted-foreground">
                {summary.gaps.excluded > 0 && summary.gaps.invited > 0 ? (
                  <T
                    kind="{{excluded}} of them won't receive an invitation."
                    excluded={summary.gaps.excluded}
                  />
                ) : (
                  <T
                    kind={bodyFor(summary.severity, summary.access, summary.gaps, summary.soleName)}
                  />
                )}
                {summary.later.count > 0 && <> {startsLaterHeadline(summary.later)}</>}
              </Typography>
            </div>
            <Button
              variant="outlined"
              className="bg-card"
              onClick={onViewDetails}
              data-intercom-target="share-license-alert-view-details"
            >
              <T kind="View details" />
            </Button>
          </div>
        </Alert>
      );
    })
    .getOrElse(() => <></>);
}

export default observer(LicenseAlertBanner);
